import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getBackendClient } from "@/lib/backend";
import { firstRecord, isRecord } from "@/lib/utils/query-helpers";
import { fetchCustomerDocumentView, renderCustomerMessage } from "./assistant-customer-copy";

export type AssistantDocumentKind = "estimate" | "invoice" | "purchase_order";

interface AssistantDocumentProposalBase {
  conversationId: string;
  documentKind: AssistantDocumentKind;
  editableFields: Record<string, unknown>;
  expiresAt: string;
  preview: Record<string, unknown>;
  proposalId: string;
  revision: number;
}

export interface ReadyAssistantDocumentProposal extends AssistantDocumentProposalBase {
  status: "ready";
}

export interface CompletedAssistantDocumentProposal extends AssistantDocumentProposalBase {
  result: Required<Pick<ProposalCreationResult, "documentId" | "documentKind">> &
    ProposalCreationResult;
  status: "completed";
}

export type AssistantDocumentProposal =
  | ReadyAssistantDocumentProposal
  | CompletedAssistantDocumentProposal;

export interface ApprovalChallenge {
  approvalCommand: string;
  challenge: string;
  expiresAt: string;
  proposalId: string;
  revision: number;
}

export interface ProposalCreationResult {
  documentId?: string;
  documentKind?: AssistantDocumentKind;
  documentNumber?: string;
  path?: string;
}

const backend = getBackendClient();
const SAFE_ERROR = "Proposal action is unavailable. Try again from this conversation.";

function string(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function isValidProposalBase(record: Record<string, unknown>): boolean {
  return (
    ["estimate", "invoice", "purchase_order"].includes(string(record.documentKind) ?? "") &&
    Boolean(string(record.proposalId)) &&
    Boolean(string(record.conversationId)) &&
    Number.isInteger(record.revision) &&
    Boolean(string(record.expiresAt)) &&
    isRecord(record.editableFields) &&
    isRecord(record.preview)
  );
}

function isCompletedProposalResult(record: Record<string, unknown>): boolean {
  if (record.status !== "completed") return false;
  if (!isRecord(record.result)) return false;
  if (!string(record.result.documentId)) return false;
  const kind = string(record.result.documentKind) ?? "";
  if (!["estimate", "invoice", "purchase_order"].includes(kind)) return false;
  return record.result.documentKind === record.documentKind;
}

function completedProposalFrom(
  record: Record<string, unknown>,
): CompletedAssistantDocumentProposal | null {
  if (!isCompletedProposalResult(record)) return null;
  const result = record.result as ProposalCreationResult;
  return {
    ...(record as unknown as AssistantDocumentProposalBase),
    result: { ...result, path: pathFor(result) } as CompletedAssistantDocumentProposal["result"],
    status: "completed",
  };
}

function proposal(value: unknown): AssistantDocumentProposal | null {
  const record = firstRecord(value);
  if (!record || !isValidProposalBase(record)) return null;
  if (record.status === "ready" && record.result === null) {
    return record as unknown as ReadyAssistantDocumentProposal;
  }
  return completedProposalFrom(record);
}

function response(value: unknown): Record<string, unknown> {
  const record = firstRecord(value);
  if (!record || record.ok !== true) throw new Error(SAFE_ERROR);
  return record;
}

function pathFor(result: ProposalCreationResult): string | undefined {
  if (string(result.path)) return result.path;
  if (!result.documentId || !result.documentKind) return undefined;
  const roots: Record<AssistantDocumentKind, string> = {
    estimate: "/estimates",
    invoice: "/invoices",
    purchase_order: "/purchase-orders",
  };
  return `${roots[result.documentKind]}/${result.documentId}`;
}

async function rpc(
  name: string,
  params: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const { data, error } = await backend.data.rpc(name, params);
  if (error) throw new Error(SAFE_ERROR);
  return response(data);
}

function isValidChallengeResponse(
  data: Record<string, unknown>,
  item: ReadyAssistantDocumentProposal,
): boolean {
  return (
    string(data.proposalId) === item.proposalId &&
    data.revision === item.revision &&
    Boolean(string(data.challenge)) &&
    Boolean(string(data.expiresAt)) &&
    Boolean(string(data.approvalCommand))
  );
}

export function useDocumentProposals(conversationId: string | null) {
  const queryClient = useQueryClient();
  const queryKey = ["assistant-document-proposals", conversationId] as const;
  const proposalsQuery = useQuery({
    enabled: Boolean(conversationId),
    gcTime: 0,
    queryKey,
    queryFn: async () => {
      const data = await rpc("assistant_list_document_proposals", {
        p_conversation_id: conversationId,
      });
      if (!Array.isArray(data.proposals)) throw new Error(SAFE_ERROR);
      return data.proposals
        .map(proposal)
        .filter((item): item is AssistantDocumentProposal => item !== null);
    },
    refetchInterval: 2_000,
    retry: false,
  });
  const refresh = async () => queryClient.invalidateQueries({ queryKey });
  const challenge = useMutation({
    mutationFn: async (item: ReadyAssistantDocumentProposal): Promise<ApprovalChallenge> => {
      const data = await rpc("assistant_issue_document_approval_challenge", {
        p_proposal_id: item.proposalId,
        p_revision: item.revision,
      });
      if (!isValidChallengeResponse(data, item)) throw new Error(SAFE_ERROR);
      return data as unknown as ApprovalChallenge;
    },
    onSuccess: refresh,
    retry: false,
  });
  const approve = useMutation({
    mutationFn: async ({
      item,
      challenge: approvalChallenge,
    }: {
      item: ReadyAssistantDocumentProposal;
      challenge: string;
    }) => {
      const data = await rpc("assistant_approve_document_proposal", {
        p_challenge: approvalChallenge,
        p_proposal_id: item.proposalId,
        p_revision: item.revision,
      });
      if (data.status !== "created" || !isRecord(data.result)) throw new Error(SAFE_ERROR);
      const result = data.result as ProposalCreationResult;
      return { ...result, path: pathFor(result) };
    },
    onSuccess: refresh,
    retry: false,
  });
  const cancel = useMutation({
    mutationFn: async (item: ReadyAssistantDocumentProposal) => {
      const data = await rpc("assistant_cancel_document_proposal", {
        p_proposal_id: item.proposalId,
        p_revision: item.revision,
      });
      if (data.status !== "canceled") throw new Error(SAFE_ERROR);
    },
    onSuccess: refresh,
    retry: false,
  });
  const customerMessage = useMutation({
    mutationFn: async (item: AssistantDocumentProposal) =>
      renderCustomerMessage(
        await fetchCustomerDocumentView({ proposalId: item.proposalId, revision: item.revision }),
      ),
    retry: false,
  });

  return {
    approve: approve.mutateAsync,
    cancel: cancel.mutateAsync,
    customerMessage: customerMessage.mutateAsync,
    challenge: challenge.mutateAsync,
    error: proposalsQuery.error ? SAFE_ERROR : undefined,
    proposals: proposalsQuery.data ?? [],
    refresh,
  };
}
